import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ProtectedRoute } from './auth/ProtectedRoute.jsx';
import { RequireAdmin } from './auth/RequireAdmin.jsx';
import { RequireNotDealer } from './auth/RequireNotDealer.jsx';
import { TenantProvider } from './components/TenantContext.jsx';
import { BuildJobsProvider } from './components/IvrAgents/BuildJobsContext.jsx';
import Layout from './components/Layout.jsx';
import LoginPage from './pages/LoginPage.jsx';
import TenantsListPage from './pages/TenantsListPage.jsx';
import TenantEditPage from './pages/TenantEditPage.jsx';
import CallsPage from './pages/CallsPage.jsx';
import CallDetailPage from './pages/CallDetailPage.jsx';
import DirectoryPage from './pages/DirectoryPage.jsx';
import CallTagsPage from './pages/CallTagsPage.jsx';
import CustomersPage from './pages/CustomersPage.jsx';
import OutboundPage from './pages/OutboundPage.jsx';
import OutboundCampaignDetailPage from './pages/OutboundCampaignDetailPage.jsx';
import MembersPage from './pages/MembersPage.jsx';
import SystemHealthPage from './pages/SystemHealthPage.jsx';
import ApiDocsPage from './pages/ApiDocsPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import MinuteRecordsPage from './pages/MinuteRecordsPage.jsx';
import PlansPage from './pages/PlansPage.jsx';
import IvrAgentsListPage from './pages/IvrAgents/IvrAgentsListPage.jsx';
import IvrAgentWorkspacePage from './pages/IvrAgents/IvrAgentWorkspacePage.jsx';
import SurveyDataIndexPage from './pages/IvrAgents/SurveyDataIndexPage.jsx';
import SurveyDataPage from './pages/IvrAgents/SurveyDataPage.jsx';
import { CustomerLeadsPage } from './pages/CustomerLeadsPage.jsx';
import HomeRedirect from './pages/HomeRedirect.jsx';
import { RequireSuperAdmin } from './auth/RequireSuperAdmin.jsx';
import TenantsOverviewPage from './pages/SuperAdmin/TenantsOverviewPage.jsx';
import TenantOpsPage from './pages/SuperAdmin/TenantOpsPage.jsx';
import CustomerDataPage from './pages/SuperAdmin/CustomerDataPage.jsx';
import ReceivablesPage from './pages/SuperAdmin/ReceivablesPage.jsx';
import LoadMinutesPage from './pages/SuperAdmin/LoadMinutesPage.jsx';
import TicketsPage from './pages/SuperAdmin/TicketsPage.jsx';
import DistributorsPage from './pages/SuperAdmin/DistributorsPage.jsx';
import { RequireDealer } from './auth/RequireDealer.jsx';
import DealerDiagnosePage from './pages/Dealer/DealerDiagnosePage.jsx';
import { RequireTecom } from './auth/RequireTecom.jsx';
import Sdx500LicensePage from './pages/Sdx500LicensePage.jsx';
import Sdx500SignupPage from './pages/Sdx500SignupPage.jsx';
import MyLicensePage from './pages/MyLicensePage.jsx';

/** SPA 換頁回到頁頂 — React Router 預設保留捲動位置，點進新頁會停在原本的捲動深度 */
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

export default function App() {
    return (
        <BuildJobsProvider>
            <ScrollToTop />
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/sdx500-signup" element={<Sdx500SignupPage />} />
                <Route
                    element={
                        <ProtectedRoute>
                            <TenantProvider>
                                <Layout />
                            </TenantProvider>
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<HomeRedirect />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/dashboard/minutes" element={<MinuteRecordsPage />} />
                    <Route path="/plans" element={<RequireAdmin><RequireNotDealer><PlansPage /></RequireNotDealer></RequireAdmin>} />
                    <Route path="/calls" element={<CallsPage />} />
                    <Route path="/calls/:id" element={<CallDetailPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/outbound" element={<OutboundPage />} />
                    <Route path="/outbound/:campaignId" element={<OutboundCampaignDetailPage />} />
                    <Route path="/directory" element={<RequireAdmin><DirectoryPage /></RequireAdmin>} />
                    <Route path="/tags" element={<RequireAdmin><CallTagsPage /></RequireAdmin>} />
                    <Route path="/tenants" element={<RequireAdmin><TenantsListPage /></RequireAdmin>} />
                    <Route path="/tenants/:hostId" element={<RequireAdmin><TenantEditPage /></RequireAdmin>} />
                    <Route path="/members" element={<RequireAdmin><MembersPage /></RequireAdmin>} />
                    <Route path="/sdx500" element={<RequireTecom><Sdx500LicensePage /></RequireTecom>} />
                    <Route path="/sdx500-distributors" element={<RequireTecom><DistributorsPage /></RequireTecom>} />
                    <Route path="/my-license" element={<MyLicensePage />} />
                    <Route path="/ivr-agents" element={<IvrAgentsListPage />} />
                    {/* 工作室開放子帳號，但頁內依指派 scope 把關 + 唯讀（只能模擬通話） */}
                    <Route path="/ivr-agents/:avatarId" element={<IvrAgentWorkspacePage />} />
                    <Route path="/survey-data" element={<SurveyDataIndexPage />} />
                    <Route path="/survey-data/:avatarId" element={<SurveyDataPage />} />
                    <Route path="/leads" element={<CustomerLeadsPage />} />
                    {/* /system = 系統狀態頁，母帳號可看（0724 曾收成 super_admin 限定，
                        但入口只有哨兵徽章 popover 的「查看完整系統狀態」→ 連同徽章一起等於整組消失）。
                        跨租戶紅字的顧慮由徽章的 tenant scoping（08ff2c4）處理。 */}
                    <Route path="/system" element={<RequireAdmin><SystemHealthPage /></RequireAdmin>} />
                    <Route path="/api-docs" element={<RequireAdmin><ApiDocsPage /></RequireAdmin>} />
                    <Route path="/admin" element={<RequireSuperAdmin><TenantsOverviewPage /></RequireSuperAdmin>} />
                    <Route path="/admin/data" element={<RequireSuperAdmin><CustomerDataPage /></RequireSuperAdmin>} />
                    <Route path="/admin/business" element={<RequireSuperAdmin><CustomerDataPage role="business" /></RequireSuperAdmin>} />
                    <Route path="/admin/billing" element={<RequireSuperAdmin><ReceivablesPage /></RequireSuperAdmin>} />
                    <Route path="/admin/minutes" element={<RequireSuperAdmin><LoadMinutesPage /></RequireSuperAdmin>} />
                    <Route path="/admin/tenants/:hostId" element={<RequireSuperAdmin><TenantOpsPage /></RequireSuperAdmin>} />
                    <Route path="/admin/tenants/:hostId/calls" element={<RequireSuperAdmin><CallsPage /></RequireSuperAdmin>} />
                    <Route path="/admin/tenants/:hostId/calls/:id" element={<RequireSuperAdmin><CallDetailPage /></RequireSuperAdmin>} />
                    <Route path="/admin/tickets" element={<RequireSuperAdmin><TicketsPage /></RequireSuperAdmin>} />
                    <Route path="/dealer/diagnose" element={<RequireDealer><DealerDiagnosePage /></RequireDealer>} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BuildJobsProvider>
    );
}
